import React from 'react';
import './LoadingIndicator.css';

export interface LoadingIndicatorProps {
  /** Type of loading indicator */
  variant?: 'spinner' | 'dots' | 'pulse' | 'skeleton' | 'progress';
  /** Size of the indicator */
  size?: 'small' | 'medium' | 'large';
  /** Loading message to display */
  message?: string;
  /** Progress percentage (0-100) for progress variant */
  progress?: number;
  /** Color theme */
  color?: 'primary' | 'secondary' | 'accent';
  /** Whether to show as overlay */
  overlay?: boolean;
  /** Custom className */
  className?: string;
}

export const LoadingIndicator: React.FC<LoadingIndicatorProps> = ({
  variant = 'spinner',
  size = 'medium',
  message,
  progress = 0,
  color = 'primary',
  overlay = false,
  className = ''
}) => {
  const baseClass = 'loading-indicator';
  const variantClass = `${baseClass}--${variant}`;
  const sizeClass = `${baseClass}--${size}`;
  const colorClass = `${baseClass}--${color}`;
  const overlayClass = overlay ? `${baseClass}--overlay` : '';
  
  const classes = [baseClass, variantClass, sizeClass, colorClass, overlayClass, className]
    .filter(Boolean)
    .join(' ');

  const renderIndicator = () => {
    switch (variant) {
      case 'spinner':
        return (
          <div className="loading-spinner">
            <div className="loading-spinner__circle"></div>
          </div>
        );
      
      case 'dots':
        return (
          <div className="loading-dots">
            <div className="loading-dots__dot"></div>
            <div className="loading-dots__dot"></div>
            <div className="loading-dots__dot"></div>
          </div>
        );
      
      case 'pulse':
        return (
          <div className="loading-pulse">
            <div className="loading-pulse__circle"></div>
          </div>
        );
      
      case 'skeleton':
        return (
          <div className="loading-skeleton">
            <div className="loading-skeleton__line loading-skeleton__line--title"></div>
            <div className="loading-skeleton__line loading-skeleton__line--text"></div>
            <div className="loading-skeleton__line loading-skeleton__line--text"></div>
          </div>
        );
      
      case 'progress':
        return (
          <div className="loading-progress">
            <div className="loading-progress__track">
              <div 
                className="loading-progress__fill"
                style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
              ></div>
            </div>
            {progress > 0 && (
              <div className="loading-progress__text">
                {Math.round(progress)}%
              </div>
            )}
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className={classes} role="status" aria-live="polite">
      {renderIndicator()}
      {message && (
        <div className="loading-indicator__message">
          {message}
        </div>
      )}
      <span className="sr-only">Loading...</span>
    </div>
  );
};

interface LoadingOverlayProps {
  /** Whether the overlay is visible */
  visible: boolean;
  /** Loading indicator props */
  indicatorProps?: Omit<LoadingIndicatorProps, 'overlay'>;
  /** Background opacity (0-1) */
  backgroundOpacity?: number;
  /** Whether to blur the background */
  blur?: boolean;
}

export const LoadingOverlay: React.FC<LoadingOverlayProps> = ({
  visible,
  indicatorProps = {},
  backgroundOpacity = 0.7,
  blur = false
}) => {
  if (!visible) return null;

  const overlayStyle = {
    '--overlay-opacity': backgroundOpacity,
    '--overlay-blur': blur ? 'blur(2px)' : 'none'
  } as React.CSSProperties;

  return (
    <div className="loading-overlay" style={overlayStyle}>
      <LoadingIndicator {...indicatorProps} />
    </div>
  );
};

interface LoadingButtonProps {
  /** Whether the button is in loading state */
  loading: boolean;
  /** Button content when not loading */
  children: React.ReactNode;
  /** Loading message */
  loadingText?: string;
  /** Button props */
  [key: string]: any;
}

export const LoadingButton: React.FC<LoadingButtonProps> = ({
  loading,
  children,
  loadingText = 'Loading...',
  disabled,
  className = '',
  ...props
}) => {
  return (
    <button
      {...props}
      disabled={disabled || loading}
      className={`loading-button ${loading ? 'loading-button--loading' : ''} ${className}`}
    >
      {loading && (
        <LoadingIndicator
          variant="spinner"
          size="small"
          className="loading-button__indicator"
        />
      )}
      <span className={loading ? 'loading-button__text--hidden' : ''}>
        {loading ? loadingText : children}
      </span>
    </button>
  );
};

export default LoadingIndicator;